import { z } from 'zod';
// ── delivery routing region (operational, not strictly geographic) ───────────
export const regionSchema = z.enum([
    'north',
    'south',
    'east',
    'west',
    'center',
]);
// ── customers ────────────────────────────────────────────────────────────────
export const customerTypeSchema = z.enum(['individual', 'company']);
// Phone numbers are stored NORMALIZED: whitespace stripped, then only digits,
// one optional leading "+" and "-" separators. Historic free-text entries like
// "57939155/57961023" or "5764 2402 / 5783 5628" are no longer accepted — a
// second number belongs in `altPhones`. Normalizing here (transform) means the
// (companyId, phone) unique constraint actually bites on re-entries that only
// differ by spacing.
export const PHONE_ALLOWED_RE = /^\+?\d[\d-]*\d$/;
export function normalizePhone(raw) {
    return raw.replace(/\s+/g, '');
}
export const phoneSchema = z
    .string()
    .transform(normalizePhone)
    .pipe(z
    .string()
    .min(4)
    .max(20)
    .regex(PHONE_ALLOWED_RE, 'Phone may only contain digits, "+" and "-".'));
// Extra contact numbers for the same customer (landline, spouse, second GSM…).
export const altPhonesSchema = z.array(phoneSchema).max(4);
export const customerCreateSchema = z
    .object({
    type: customerTypeSchema.default('individual'),
    // Required for both — for individuals this is the customer's name, for
    // company customers this is the primary contact's name.
    name: z.string().min(1).max(200),
    phone: phoneSchema,
    altPhones: altPhonesSchema.default([]),
    email: z.string().email().optional().nullable(),
    // Company fields (only when type === 'company')
    companyName: z.string().max(200).optional().nullable(),
    vatNumber: z.string().max(60).optional().nullable(),
    brn: z.string().max(60).optional().nullable(),
    contactPerson: z.string().max(200).optional().nullable(),
    // Free-text NIC or passport. Optional. Useful for individuals; B2B can
    // still use it for the primary contact's NIC.
    nationalId: z.string().max(60).optional().nullable(),
    // Date of birth (individuals only — optional). Captured for birthday
    // outreach and verification on big-ticket purchases.
    dateOfBirth: z.coerce.date().optional().nullable(),
    // Tax treatment
    vatExcluded: z.boolean().default(false),
    // Address
    address: z.string().max(500).optional().nullable(),
    // Precise / landmark address (free text) for when the map can't pinpoint the
    // exact spot. Captured on the customer; printed on their delivery documents.
    addressPrecise: z.string().max(1000).optional().nullable(),
    city: z.string().max(120).optional().nullable(),
    country: z.string().max(120).optional().nullable().default('Mauritius'),
    // Optional geo-pin (from the map picker). lat / lng bounded to the
    // valid WGS84 range so a typo doesn't produce a pin in space.
    latitude: z.number().gte(-90).lte(90).optional().nullable(),
    longitude: z.number().gte(-180).lte(180).optional().nullable(),
    // Delivery routing region. Auto-suggested from city; sales girl confirms.
    region: regionSchema.optional().nullable(),
    // Prefs
    notes: z.string().max(1000).optional().nullable(),
    preferredChannel: z.enum(['whatsapp', 'email', 'print', 'download', 'sms']).default('whatsapp'),
    preferredShopId: z.string().optional().nullable(),
})
    .refine((v) => v.type !== 'company' || (v.companyName && v.companyName.length > 0), { message: 'Company name is required for company customers.', path: ['companyName'] });
export const customerUpdateSchema = z.object({
    type: customerTypeSchema.optional(),
    name: z.string().min(1).max(200).optional(),
    phone: phoneSchema.optional(),
    altPhones: altPhonesSchema.optional(),
    email: z.string().email().optional().nullable(),
    companyName: z.string().max(200).optional().nullable(),
    vatNumber: z.string().max(60).optional().nullable(),
    brn: z.string().max(60).optional().nullable(),
    contactPerson: z.string().max(200).optional().nullable(),
    nationalId: z.string().max(60).optional().nullable(),
    dateOfBirth: z.coerce.date().optional().nullable(),
    vatExcluded: z.boolean().optional(),
    address: z.string().max(500).optional().nullable(),
    addressPrecise: z.string().max(1000).optional().nullable(),
    city: z.string().max(120).optional().nullable(),
    country: z.string().max(120).optional().nullable(),
    latitude: z.number().gte(-90).lte(90).optional().nullable(),
    longitude: z.number().gte(-180).lte(180).optional().nullable(),
    region: regionSchema.optional().nullable(),
    notes: z.string().max(1000).optional().nullable(),
    preferredChannel: z.enum(['whatsapp', 'email', 'print', 'download', 'sms']).optional(),
    preferredShopId: z.string().optional().nullable(),
    isActive: z.boolean().optional(),
});
// ── Fast Track (priority) delivery ───────────────────────────────────────────
// Kalachand T&C §9: a customer can opt into priority delivery for a flat,
// VAT-INCLUSIVE fee that the system adds automatically to the invoice. The fee
// is server-authoritative (the client only sends a boolean) so a forged amount
// can never stick. Held at a flat Rs 2,000 for now; the policy range is
// Rs 1,500–2,500 (location/urgency tiers are a later enhancement).
export const FAST_TRACK_DELIVERY_FEE = 2000;
/** Standard Mauritius VAT rate applied to the (VAT-inclusive) fast-track fee. */
export const FAST_TRACK_VAT_RATE = 0.15;
// ── orders ───────────────────────────────────────────────────────────────────
export const fulfillmentTypeSchema = z.enum(['pickup', 'delivery']);
// Custom mattress size bounds (cm). A made-to-order local mattress can be built
// to a bespoke W×L; the price is derived from the model's standard size ladder.
export const CUSTOM_SIZE_MIN_CM = 60;
export const CUSTOM_SIZE_MAX_CM = 250;
// ── Special Imported Dimensions (Kalachand T&C §8) ───────────────────────────
// A bespoke W×L on an IMPORTED mattress carries a surcharge over the matched
// standard-size price, tiered by area against 150×200 cm (30,000 cm²): at or
// below the threshold → +20%, above it → +25%. The surcharge does NOT apply to
// locally made-to-order custom sizes (those keep plain size-ladder pricing).
export const IMPORTED_DIM_SURCHARGE_AREA_THRESHOLD_CM2 = 150 * 200; // 30,000
export const IMPORTED_DIM_SURCHARGE_PCT_AT_OR_BELOW = 0.2;
export const IMPORTED_DIM_SURCHARGE_PCT_ABOVE = 0.25;
/** Surcharge fraction (0.20 / 0.25) for a special imported dimension by area. */
export function importedDimensionSurchargePct(widthCm, lengthCm) {
    return widthCm * lengthCm <= IMPORTED_DIM_SURCHARGE_AREA_THRESHOLD_CM2
        ? IMPORTED_DIM_SURCHARGE_PCT_AT_OR_BELOW
        : IMPORTED_DIM_SURCHARGE_PCT_ABOVE;
}
// A special imported dimension can only be amended within this many days of the
// order being placed (T&C §8).
export const IMPORTED_DIM_AMENDMENT_WINDOW_DAYS = 4;
// The sales girl's pick of WHICH size to give for a model gift (a gift whose
// ProductModelGift references a giftModel with several sizes). `giftModelId` keys
// the gift being resolved; `productId` is the chosen sellable variant of it. Only
// needed for model gifts — fixed-variant gifts carry no selection.
export const giftSelectionSchema = z.object({
    giftModelId: z.string().min(1),
    productId: z.string().min(1),
});
export const orderLineInputSchema = z
    .object({
    productId: z.string().min(1),
    qty: z.number().int().positive(),
    unitPrice: z.coerce.number().nonnegative().optional(), // defaults to product price
    discount: z.coerce.number().nonnegative().default(0),
    // Made-to-order selections (e.g. local mattress colour + side colour + fabric + density).
    selectedColor: z.string().max(40).nullable().optional(),
    selectedSideColor: z.string().max(40).nullable().optional(),
    selectedFabric: z.string().max(40).nullable().optional(),
    selectedDensity: z.string().max(40).nullable().optional(),
    // Custom mattress size (cm) for a made-to-order local mattress. When both are
    // set, the server IGNORES any client unitPrice and derives the price from the
    // model's standard size ladder. `productId` is the standard variant the price
    // was matched against. Must be supplied together (both or neither).
    customWidthCm: z.coerce.number().int().min(CUSTOM_SIZE_MIN_CM).max(CUSTOM_SIZE_MAX_CM).optional(),
    customLengthCm: z.coerce.number().int().min(CUSTOM_SIZE_MIN_CM).max(CUSTOM_SIZE_MAX_CM).optional(),
    // Explicit backorder: reserve this line against incoming HQ stock when the
    // imported item is out of stock (sales girl opts in). Forces delivery.
    backorder: z.boolean().optional().default(false),
    // Hand this stocked line over at the counter when the order confirms — the
    // customer takes the selling shop's unit now; the rest of the order goes
    // out on delivery. Stock is validated + drawn from the SELLING SHOP, and an
    // in-store handover (DELIVERED delivery note) is recorded automatically.
    // Only honoured on delivery orders for in-stock STOCK_AT_LOCATION lines.
    handoverNow: z.boolean().optional().default(false),
    // Opt in to the model's free gift(s) for this line. Gifts are NO LONGER
    // auto-allocated — the sales girl explicitly ticks each gifting line to add
    // it. Default false (nothing given away unless chosen). Ignored for lines
    // whose model has no linked gift.
    includeGift: z.boolean().optional().default(false),
    // Chosen size for each MODEL gift on this line (a gift the admin left open so
    // the sales girl picks the size). One entry per model gift; ignored for fixed-
    // variant gifts. Required (server-enforced) when the line opts into a model
    // gift — otherwise the server can't know which size to give.
    giftSelections: z.array(giftSelectionSchema).optional().default([]),
})
    .refine((l) => (l.customWidthCm == null) === (l.customLengthCm == null), {
    message: 'customWidthCm and customLengthCm must be supplied together',
    path: ['customWidthCm'],
});
export const orderCreateSchema = z
    .object({
    shopId: z.string().min(1),
    customerId: z.string().min(1),
    // Salesperson the sale is attributed to (drives commission). Only honoured
    // for cross-shop creators (owner/admin/ops with orders.view_all_shops); a
    // normal sales girl always sells as herself. REQUIRED when a cross-shop user
    // sells for a branch SHOP — enforced in orders.service.createDraft.
    salespersonId: z.string().min(1).optional(),
    fulfillmentType: fulfillmentTypeSchema,
    lines: z.array(orderLineInputSchema).min(1),
    // pickup: optional alternate pickup shop. If not set, customer picks up at selling shop.
    pickupShopId: z.string().optional().nullable(),
    // delivery: address + preferred date (+ how many old mattresses to dispose of)
    deliveryAddress: z.string().max(500).optional().nullable(),
    // Optional full / precise address for when the map only finds the nearest
    // point or city. Shown on the invoice + delivery note when present.
    deliveryAddressPrecise: z.string().max(1000).optional().nullable(),
    deliveryDate: z.coerce.date().optional().nullable(),
    // Routing region snapshot — defaults to customer.region; sales girl can
    // override per-order (e.g. delivering to a different address).
    region: regionSchema.optional().nullable(),
    // Owner/admin override that lets the booking skip the daily delivery cap.
    // Backend gates on the `scheduling.override_capacity` permission.
    productionUrgentOverride: z.boolean().optional().default(false),
    // 0 = none. We offer this as a free service but track quantity so we
    // know how many we've collected over time.
    disposalQty: z.coerce.number().int().min(0).max(20).default(0),
    // Opt into Fast Track (priority) delivery — adds the flat VAT-inclusive fee.
    // The amount is fixed server-side; this is just the on/off switch.
    fastTrack: z.boolean().optional().default(false),
    notes: z.string().max(1000).optional().nullable(),
    // Customer accepts that a custom IMPORTED mattress on this order takes time to
    // arrive and is happy to receive everything together — overrides the guard
    // that otherwise asks to split fast items onto a separate order.
    allowImportMix: z.boolean().optional().default(false),
    // The sales girl has been warned that a warehouse-only line (imported
    // mattress / sofa bed) is out of stock at HQ and will be RESERVED against
    // the next arrival rather than taken off a shop floor. Without it the
    // server raises `reservation_required` so she is never left promising a
    // delivery date the warehouse can't meet. See orders/stock-validation.ts.
    allowBackorderReservation: z.boolean().optional().default(false),
})
    .refine((v) => v.fulfillmentType !== 'delivery' || (v.deliveryAddress && v.deliveryAddress.trim().length > 0), { message: 'A delivery address is required for delivery orders.', path: ['deliveryAddress'] });
export const orderUpdateLinesSchema = z.object({
    lines: z.array(orderLineInputSchema).min(1),
});
// Edit the free-text internal note on an order at any status. Low-risk metadata
// (things operations / finance should see) — captured at creation and amendable
// afterwards from the order detail page.
export const orderNotesUpdateSchema = z.object({
    notes: z.string().max(1000).nullable(),
});
// Set / change the fulfilment on an existing DRAFT order (the same fields the
// wizard captures at step 3). Used after an over-cap discount is owner-approved:
// the salesperson sets delivery details on the order page BEFORE taking the
// deposit. Mirrors the fulfilment half of orderCreateSchema / quotationConvert.
export const orderFulfilmentUpdateSchema = z
    .object({
    fulfillmentType: fulfillmentTypeSchema,
    pickupShopId: z.string().optional().nullable(),
    deliveryAddress: z.string().max(500).optional().nullable(),
    deliveryAddressPrecise: z.string().max(1000).optional().nullable(),
    deliveryDate: z.coerce.date().optional().nullable(),
    region: regionSchema.optional().nullable(),
    productionUrgentOverride: z.boolean().optional().default(false),
    disposalQty: z.coerce.number().int().min(0).max(20).default(0),
})
    .refine((v) => v.fulfillmentType !== 'delivery' || (v.deliveryAddress && v.deliveryAddress.trim().length > 0), { message: 'A delivery address is required for delivery orders.', path: ['deliveryAddress'] });
export const orderCancelSchema = z.object({
    reason: z.string().min(1).max(500),
    // When true, the non-refundable deposit is ALSO reversed by a credit note —
    // for a company-side cancellation (damaged goods / wrong price) or a goodwill
    // refund. Default (false) keeps the deposit (forfeited) per the T&C.
    reverseDeposit: z.boolean().optional(),
    // Whether the goods return to sellable stock. Default (true) restocks —
    // good-condition return / never delivered. Pass false for a damaged-goods
    // refund: the units are written off, not returned to stock.
    restock: z.boolean().optional(),
});
// ── discount approvals (owner sign-off for over-cap discounts) ───────────────
// A salesperson may grant up to the company's salesDiscountCapPct on their own
// authority. Above that they request an owner DiscountApproval against the DRAFT
// order; the request is recomputed from the stored draft (never trusts the
// client) and lands in the owner's approvals inbox.
/** Default sales-discount cap (fraction of gross) when a company has none set. */
export const DEFAULT_SALES_DISCOUNT_CAP_PCT = 0.03;
export const discountApprovalStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED']);
/**
 * Effective order discount as a fraction of gross (qty×unit, VAT-inclusive).
 * `gross` is the pre-discount all-in amount; for a stored order it equals
 * `total + discountTotal` (total is already net of discount). Guards against a
 * zero / non-finite gross so callers never divide by zero.
 */
export function effectiveDiscountPct(gross, discountTotal) {
    if (!Number.isFinite(gross) || gross <= 0)
        return 0;
    return Math.max(0, discountTotal) / gross;
}
// Salesperson asks for owner sign-off on a draft order. `reason` is their
// justification; the requested amount is recomputed from the draft server-side.
export const discountApprovalRequestSchema = z.object({
    orderId: z.string().min(1),
    reason: z.string().max(1000).optional().nullable(),
});
// Owner approves — optionally granting a different ceiling than requested
// (defaults to the requested pct server-side when omitted). Fraction of gross.
export const discountApprovalApproveSchema = z.object({
    approvedPct: z.coerce.number().min(0).max(1).optional(),
    note: z.string().max(1000).optional().nullable(),
});
export const discountApprovalRejectSchema = z.object({
    note: z.string().min(1, 'A reason is required to reject').max(1000),
});
// ── payments ─────────────────────────────────────────────────────────────────
export const paymentMethodSchema = z.enum(['cash', 'juice', 'emtel', 'myt', 'bank_transfer', 'card', 'cheque', 'other']);
// Electronic / traceable methods must carry a reference (Juice/Emtel/MYT ref,
// card receipt, cheque number, bank-transfer ref). Cash + "other" are exempt.
export const METHODS_REQUIRING_REFERENCE = ['juice', 'emtel', 'myt', 'bank_transfer', 'card', 'cheque'];
export const paymentRecordSchema = z
    .object({
    amount: z.coerce.number().positive(),
    method: paymentMethodSchema,
    reference: z.string().max(120).optional().nullable(),
    // When true, this is a delivery-COD payment collected by a driver in the
    // field. The GL posting routes to 1150 Cash in Transit instead of a shop
    // drawer; the cash is settled later by a DriverRemittance acknowledgement.
    isDeliveryCod: z.boolean().optional(),
})
    .refine((v) => !METHODS_REQUIRING_REFERENCE.includes(v.method) || !!v.reference?.trim(), {
    message: 'A reference / receipt number is required for Juice, bank transfer, card, and cheque payments.',
    path: ['reference'],
});
// Mixed / split tender: one settlement paid across several methods (e.g. part
// cash + part Juice). Each line is a full payment that records + posts on its
// own; the backend writes them all in ONE transaction so they can't half-apply.
export const paymentBatchSchema = z.object({
    payments: z.array(paymentRecordSchema).min(1).max(6),
});
// ── legacy order intake (pre-go-live paper orders) ───────────────────────────
// Orders written on the paper invoice book before the 1 Jul 2026 go-live, whose
// delivery falls on or after it. The customer keeps quoting the manual invoice
// number; production, delivery, payments and the VAT invoice move into the
// system. See modules/sales/orders/legacy-intake.service.ts.
//
// Deliberately a SEPARATE schema on a separate endpoint rather than a flag on
// orderCreateSchema: the legacy path bypasses stock, capacity and past-date
// validation, and one mis-set boolean on the everyday wizard endpoint would
// disable those guardrails on a real sale.
// The go-live cutoff. An order dated on or after this never qualifies as legacy
// — the intake endpoint rejects it, which makes the feature structurally
// incapable of backdating a post-go-live sale no matter who holds the
// permission. Business date (Mauritius), compared as an ISO day string.
export const LEGACY_INTAKE_CUTOFF_ISO = '2026-07-01';
// What state the goods for a legacy line were in at intake. DONE lines are
// marked READY immediately and consume no production-calendar capacity — an
// already-built mattress must not eat a July production day.
export const legacyLineStateSchema = z.enum(['NOT_STARTED', 'IN_PROGRESS', 'DONE']);
export const legacyOrderIntakeSchema = orderCreateSchema.innerType()
    .extend({
    // The manual invoice number exactly as written on the customer's paper copy.
    // Becomes Order.number verbatim — no prefix, no decoration — so the paper and
    // the system read identically.
    legacyRef: z.string().trim().min(1).max(60),
    // The date the sale was booked on paper. Must be before the go-live cutoff.
    legacyOrderDate: z.coerce.date(),
    // Cash already collected at the till before go-live. Recorded NON-POSTING:
    // it drives paidTotal / balanceDue / the payment-verification gate, but
    // writes no journal entry, because that cash is already in the opening
    // balance sheet. 0 = nothing was paid up front.
    legacyDeposit: z.coerce.number().nonnegative().default(0),
    legacyDepositMethod: paymentMethodSchema.default('cash'),
    // Per-line production state, positionally aligned to `lines`. A short or
    // empty array defaults the remaining lines to NOT_STARTED.
    lineStates: z.array(legacyLineStateSchema).default([]),
})
    .refine((v) => v.fulfillmentType !== 'delivery' || (v.deliveryAddress && v.deliveryAddress.trim().length > 0), { message: 'A delivery address is required for delivery orders.', path: ['deliveryAddress'] })
    .refine((v) => v.lineStates.length <= v.lines.length, {
    message: 'lineStates cannot have more entries than lines.',
    path: ['lineStates'],
});
// ── change order line size (ops/admin/owner) ─────────────────────────────────
// Swap a CONFIRMED (pre-delivery) order line to a different size — either another
// standard variant of the same model (`newProductId`) or a bespoke custom size
// (`customWidthCm`/`customLengthCm`). Pricing rule (server-authoritative):
//   • new size ≤ current charge → amount stays the same (no refund);
//   • new size > current charge → line rises to the new price; the difference is
//     billed now (`billing:'now'` + a `payment`) or added to the balance due
//     and collected at delivery (`billing:'at_delivery'`).
export const changeOrderSizeSchema = z
    .object({
    orderLineId: z.string().min(1),
    // EITHER a standard sibling variant…
    newProductId: z.string().min(1).optional(),
    // …OR a bespoke custom size on the same model (both sides together).
    customWidthCm: z.coerce.number().int().min(CUSTOM_SIZE_MIN_CM).max(CUSTOM_SIZE_MAX_CM).optional(),
    customLengthCm: z.coerce.number().int().min(CUSTOM_SIZE_MIN_CM).max(CUSTOM_SIZE_MAX_CM).optional(),
    // How any price INCREASE is charged (ignored when the new size isn't pricier).
    billing: z.enum(['now', 'at_delivery']).default('at_delivery'),
    // The payment taken when billing the difference immediately. Its amount is
    // ignored server-side — the exact difference is charged — but the method /
    // reference are used to post the payment.
    payment: paymentRecordSchema.optional(),
    reason: z.string().max(500).optional().nullable(),
})
    .refine((v) => (v.customWidthCm == null) === (v.customLengthCm == null), {
    message: 'customWidthCm and customLengthCm must be supplied together.',
    path: ['customWidthCm'],
})
    .refine((v) => (v.newProductId != null) !== (v.customWidthCm != null), {
    message: 'Provide either a new standard size (newProductId) or a custom size — not both, not neither.',
    path: ['newProductId'],
})
    .refine((v) => v.billing !== 'now' || !!v.payment, {
    message: 'A payment is required when billing the difference immediately.',
    path: ['payment'],
});
// ── cash close ───────────────────────────────────────────────────────────────
export const cashCloseInputSchema = z.object({
    day: z.coerce.date(),
    actualCash: z.coerce.number().nonnegative(),
    // Optional so a shop holding no cheques — and any client that predates the
    // cheque count — can close without one. Omitted means "cheques were not
    // counted", which the server records distinctly from "counted, came to zero".
    actualCheques: z.coerce.number().nonnegative().optional(),
    reason: z.string().max(500).optional(),
    shopId: z.string().min(1).optional(),
});
// ── quotations ───────────────────────────────────────────────────────────────
export const quotationStatusSchema = z.enum(['PENDING', 'FOLLOWED_UP', 'WON', 'LOST', 'EXPIRED']);
export const quotationFollowUpOutcomeSchema = z.enum([
    'CALLED',
    'NO_ANSWER',
    'CALLBACK_REQUESTED',
    'NOT_INTERESTED',
    'WHATSAPP_SENT',
    'EMAIL_SENT',
    'OTHER',
]);
export const quotationLineInputSchema = z
    .object({
    productId: z.string().min(1),
    qty: z.coerce.number().int().positive(),
    unitPrice: z.coerce.number().nonnegative(),
    discount: z.coerce.number().nonnegative().default(0),
    // Custom mattress size (cm) for a made-to-order local mattress — mirrors
    // orderLineInputSchema. When both are set the server IGNORES any client
    // unitPrice and derives the price from the model's standard size ladder;
    // `productId` is the standard variant the price was matched against. Must be
    // supplied together (both or neither).
    customWidthCm: z.coerce.number().int().min(CUSTOM_SIZE_MIN_CM).max(CUSTOM_SIZE_MAX_CM).optional(),
    customLengthCm: z.coerce.number().int().min(CUSTOM_SIZE_MIN_CM).max(CUSTOM_SIZE_MAX_CM).optional(),
    // Whether the sales girl chose to include the model's free gift with this line.
    // Stored on the quote so convert-to-order carries the same choice (gifts are
    // opt-in — see orderLineInputSchema.includeGift).
    includeGift: z.boolean().optional().default(false),
    // Chosen size(s) for the model gift(s) on this line — stored on the quote so
    // convert-to-order gives the same size the sales girl picked (mirrors
    // orderLineInputSchema.giftSelections).
    giftSelections: z.array(giftSelectionSchema).optional().default([]),
})
    .refine((l) => (l.customWidthCm == null) === (l.customLengthCm == null), {
    message: 'customWidthCm and customLengthCm must be supplied together',
    path: ['customWidthCm'],
});
export const quotationCreateSchema = z.object({
    shopId: z.string().min(1),
    customerId: z.string().min(1),
    // ISO date string; defaults to +30 days from creation when omitted.
    validUntil: z.coerce.date().optional(),
    notes: z.string().max(2000).optional().nullable(),
    // Quote the Fast Track (priority) delivery fee on the estimate. Flat amount,
    // VAT-inclusive; fixed server-side.
    fastTrack: z.boolean().optional().default(false),
    lines: z.array(quotationLineInputSchema).min(1, 'At least one line required'),
});
export const quotationFollowUpSchema = z.object({
    outcome: quotationFollowUpOutcomeSchema,
    note: z.string().max(2000).optional().nullable(),
    nextFollowUpAt: z.coerce.date().optional().nullable(),
});
// Status update — WON requires no extra fields here (conversion happens via
// the convert endpoint); LOST optionally carries a reason.
export const quotationStatusUpdateSchema = z.object({
    status: z.enum(['WON', 'LOST']),
    lostReason: z.string().max(500).optional().nullable(),
});
// Convert a WON quote into an Order — needs the same fulfillment fields the
// order normally captures. Conversion routes through ordersService.createDraft
// (the one true order path), so it mirrors orderCreateSchema's rules.
export const quotationConvertSchema = z
    .object({
    fulfillmentType: fulfillmentTypeSchema,
    // Quoted accessories the customer takes from the selling shop at payment.
    // Line IDs keep this choice separate from the quoted products and prices.
    handoverNowLineIds: z.array(z.string().min(1)).max(200).default([]),
    // Salesperson the resulting order is attributed to (drives commission).
    // Honoured only for cross-shop converters (owner/admin/ops); a sales girl
    // always sells as herself. REQUIRED when a cross-shop user converts a quote
    // for a branch SHOP — enforced downstream by createDraft (resolveSalespersonId).
    salespersonId: z.string().min(1).optional(),
    pickupShopId: z.string().optional().nullable(),
    deliveryAddress: z.string().max(500).optional().nullable(),
    deliveryAddressPrecise: z.string().max(1000).optional().nullable(),
    deliveryDate: z.coerce.date().optional().nullable(),
    region: regionSchema.optional().nullable(),
    productionUrgentOverride: z.boolean().optional().default(false),
    disposalQty: z.coerce.number().int().min(0).max(20).default(0),
    // Override the quote's Fast Track choice at convert time. Omitted → inherits
    // whatever the quotation carried (resolved in the convert handler).
    fastTrack: z.boolean().optional(),
    notes: z.string().max(2000).optional().nullable(),
    // Same warn-then-confirm gate as a new order: a warehouse-only line HQ can't
    // cover is reserved against the next arrival, and the converter is told
    // before that happens. See orderCreateSchema.allowBackorderReservation.
    allowBackorderReservation: z.boolean().optional().default(false),
    // Explicit per-product reservation choice when converting quoted stock.
    backorderProductIds: z.array(z.string().min(1)).optional().default([]),
})
    .refine((v) => v.backorderProductIds.length === 0 || v.fulfillmentType === 'delivery', { message: 'Reserved items must be delivered once stock arrives.', path: ['backorderProductIds'] })
    .refine((v) => v.fulfillmentType !== 'delivery' || (v.deliveryAddress && v.deliveryAddress.trim().length > 0), { message: 'A delivery address is required for delivery orders.', path: ['deliveryAddress'] });
//# sourceMappingURL=sales.js.map